import { Capacitor } from '@capacitor/core';

/**
 * Which of the three places this bundle is running in (BP-11 R1, R10).
 *
 * One module so that `isNative()` is answered in exactly one place. Scattering
 * `Capacitor.isNativePlatform()` through the screens is how a feature ends up
 * half-ported: the camera branch checks it, the push branch forgets, and the
 * PWA throws on a plugin that has no web implementation.
 *
 * §12's ESLint boundary is the other half of the same rule — only
 * `web/src/lib/*` may import `@capacitor/*`, so a screen that wants a native
 * capability has to come through a module like this one.
 */

/** The three answers `Capacitor.getPlatform()` can give. */
export type Platform = 'android' | 'ios' | 'web';

/** `true` inside the Capacitor WebView, `false` in a browser. */
export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/** Which platform, narrowed. Anything unexpected reads as `web`. */
export function platform(): Platform {
  const name = Capacitor.getPlatform();
  return name === 'android' || name === 'ios' ? name : 'web';
}

/**
 * The handset this is running on, or `null` in a browser.
 *
 * The narrowed form, because every native capability needs both facts at
 * once — "is this native" and "which store's rules apply" — and asking them
 * separately leaves a branch for a platform that cannot exist.
 */
export function nativePlatform(): 'android' | 'ios' | null {
  if (!isNative()) {
    return null;
  }
  const name = platform();
  return name === 'web' ? null : name;
}

/**
 * Whether a named Capacitor plugin is actually available here.
 *
 * Asked before every optional capability, because "native" and "this plugin
 * works" are different questions: a plugin can be missing from a build, and
 * a web build has implementations for some plugins and not others.
 */
export function hasPlugin(name: string): boolean {
  return Capacitor.isPluginAvailable(name);
}
